import React, { useState, useEffect, useCallback } from 'react';
import { Button, Form, InputNumber, TimePicker, message, Card, Descriptions, Tag, Space, Row, Col } from 'antd';
import { getBotStatus, startBot, stopBot, forceRunBot, updateSchedulerConfig } from '../../services/api';
import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';

dayjs.extend(customParseFormat);

const BotSettings = () => {
  const [form] = Form.useForm();
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(false);

  const fetchStatus = useCallback(async () => {
    setLoading(true);
    try {
      const response = await getBotStatus();
      setStatus(response.data);
      if (response.data.scheduler_config) {
        form.setFieldsValue({
          frequency: response.data.scheduler_config.frequency,
          active_window: [
            dayjs(response.data.scheduler_config.active_window_start, 'HH:mm'),
            dayjs(response.data.scheduler_config.active_window_end, 'HH:mm')
          ]
        });
      }
    } catch (err) {
      message.error('Failed to load bot status.');
    } finally {
      setLoading(false);
    }
  }, [form]);

  useEffect(() => {
    fetchStatus();
  }, [fetchStatus]);

  const handleControl = async (action) => {
    try {
      switch (action) {
        case 'start':
          await startBot();
          message.success('Bot started.');
          break;
        case 'stop':
          await stopBot();
          message.success('Bot stopped.');
          break;
        case 'force-run':
          await forceRunBot();
          message.success('Forcing bot run.');
          break;
        default:
          break;
      }
      fetchStatus();
    } catch (err) {
      message.error(`Failed to ${action} bot.`);
    }
  };

  const handleSaveConfig = async (values) => {
    const payload = {
      frequency: values.frequency,
      active_window_start: values.active_window[0].format('HH:mm'),
      active_window_end: values.active_window[1].format('HH:mm'),
    };
    try {
      await updateSchedulerConfig(payload);
      message.success('Scheduler configuration saved.');
      fetchStatus();
    } catch (err) {
      message.error('Failed to save configuration.');
    }
  };

  const getStatusTag = (botStatus) => {
    if (botStatus === 'running') return <Tag color="green">Running</Tag>;
    if (botStatus === 'stopped') return <Tag color="red">Stopped</Tag>;
    return <Tag color="default">Idle</Tag>;
  };

  return (
    <div>
      <h2>Bot Settings</h2>
      <Row gutter={24}>
        <Col span={12}>
          <Card title="Bot Control" loading={loading}>
            {status && (
              <>
                <Descriptions bordered column={1}>
                  <Descriptions.Item label="Status">{getStatusTag(status.status)}</Descriptions.Item>
                  <Descriptions.Item label="Last Run">{status.last_run ? new Date(status.last_run).toLocaleString() : 'N/A'}</Descriptions.Item>
                  <Descriptions.Item label="Last Error">{status.last_error || 'None'}</Descriptions.Item>
                </Descriptions>
                <Space style={{ marginTop: 24 }}>
                  <Button style={{ backgroundColor: '#52C41A', color: 'white' }} onClick={() => handleControl('start')}>Start Bot</Button>
                  <Button type="primary" danger onClick={() => handleControl('stop')}>Stop Bot</Button>
                  <Button style={{ backgroundColor: '#FAAD14', color: 'white' }} onClick={() => handleControl('force-run')}>Force Run Now</Button>
                </Space>
              </>
            )}
          </Card>
        </Col>
        <Col span={12}>
          <Card title="Scheduler Configuration" loading={loading}>
            {status && status.scheduler_config && (
              <Form form={form} layout="vertical" onFinish={handleSaveConfig}>
                <Form.Item name="frequency" label="Frequency (minutes)" rules={[{ required: true }]}>
                  <InputNumber min={1} style={{ width: '100%' }} />
                </Form.Item>
                <Form.Item name="active_window" label="Active Window" rules={[{ required: true }]}>
                  <TimePicker.RangePicker format="HH:mm" style={{ width: '100%' }} />
                </Form.Item>
                <Form.Item>
                  <Button type="primary" htmlType="submit">Save Configuration</Button>
                </Form.Item>
              </Form>
            )}
          </Card>
        </Col>
      </Row>
    </div>
  );
};
export default BotSettings;